import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Boxes, Truck, ShieldCheck, Zap, Clock, Heart, Box } from "lucide-react";
import client, { displayName } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { SearchBar } from "@/components/SearchBar";
import { TechnicianBar } from "@/components/TechnicianBar";
import { useStore } from "@/context/StoreContext";

const HERO = "https://images.pexels.com/photos/38136632/pexels-photo-38136632.jpeg";

const Section = ({ title, sub, to, children, testid }) => (
  <section data-testid={testid} className="max-w-7xl mx-auto px-4 sm:px-6 mt-12">
    <div className="flex items-end justify-between mb-5">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-graphite">{title}</h2>
        {sub && <p className="text-sm text-steel-light mt-1">{sub}</p>}
      </div>
      {to && <Link to={to} className="text-sm font-medium text-orange hover:text-orange-hover inline-flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>}
    </div>
    {children}
  </section>
);

const Home = () => {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const { recent: recentSkus } = useStore();

  useEffect(() => { client.get("/home").then((r) => setData(r.data)); }, []);
  useEffect(() => {
    if (!recentSkus.length) { setRecent([]); return; }
    client.post("/products/by-skus", { skus: recentSkus }).then((r) => setRecent(r.data.items));
  }, [recentSkus]);

  const stats = data?.stats;

  return (
    <div className="App min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-graphite">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-graphite via-graphite/90 to-graphite/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] text-orange mb-4">
              <Zap className="w-4 h-4" /> Find any part in under 10 seconds
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              The industrial catalogue<br />built for <span className="text-orange">technicians</span>.
            </h1>
            <p className="text-base sm:text-lg text-white/70 mt-5 max-w-xl">
              Search by SKU, part number or brand. Instant results, compact cards, one-handed reordering — engineered for the workshop floor.
            </p>
            <div className="mt-8 max-w-2xl"><SearchBar large /></div>
            {stats && (
              <div className="flex flex-wrap gap-x-8 gap-y-2 mt-7">
                {[["Products", stats.products], ["Categories", stats.categories], ["Brands", stats.brands]].map(([k, v]) => (
                  <div key={k} className="flex items-baseline gap-2">
                    <span className="mono text-2xl font-bold text-white">{v}</span>
                    <span className="text-xs uppercase tracking-wider text-white/50">{k}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SERVICE HIGHLIGHTS */}
      <section className="border-b border-border bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[[Zap, "Instant Search", "SKU & part number lookup"], [Truck, "Fast Fulfilment", "Quick reorder workflow"], [ShieldCheck, "OEM & Approved", "Trusted brands only"], [Clock, "Always Synced", "Live from source catalogue"]].map(([Icon, t, s], i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-white border border-border grid place-items-center shrink-0"><Icon className="w-4 h-4 text-orange" /></div>
              <div><div className="text-sm font-semibold text-graphite">{t}</div><div className="text-xs text-steel-light">{s}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* POPULAR CATEGORIES */}
      <Section title="Popular Categories" sub="Browse the catalogue by department" to="/catalogue" testid="popular-categories">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data?.popular_categories?.map((c) => (
            <Link key={c.slug} to={`/category/${c.slug}`} data-testid={`cat-${c.slug}`} className="group flex items-center justify-between p-4 bg-white border border-border rounded-md hover:border-orange hover:shadow-sm transition-all">
              <div>
                <div className="font-semibold text-graphite group-hover:text-orange transition-colors">{c.name}</div>
                <div className="mono text-xs text-steel-light mt-0.5">{c.count} parts</div>
              </div>
              <Boxes className="w-6 h-6 text-steel-light group-hover:text-orange transition-colors" />
            </Link>
          ))}
        </div>
      </Section>

      {/* RECENTLY VIEWED */}
      {recent.length > 0 && (
        <Section title="Recently Viewed" testid="recently-viewed">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {recent.slice(0, 6).map((p) => <ProductCard key={p.sku} product={p} compact />)}
          </div>
        </Section>
      )}

      {/* FREQUENTLY ORDERED */}
      <Section title="Frequently Ordered" sub="Common in-stock parts" to="/catalogue?availability=in_stock" testid="frequently-ordered">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {data?.frequently_ordered?.slice(0, 4).map((p) => <ProductCard key={p.sku} product={p} />)}
        </div>
      </Section>

      {/* FEATURED */}
      <Section title="Featured Products" sub="Latest additions to the catalogue" to="/catalogue?sort=newest" testid="featured-products">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {data?.featured_products?.slice(0, 8).map((p) => <ProductCard key={p.sku} product={p} />)}
        </div>
      </Section>

      {/* BRANDS */}
      <Section title="Brands" sub="Shop by manufacturer" testid="brands-section">
        <div className="flex flex-wrap gap-2">
          {data?.brands?.map((b) => (
            <Link key={b.slug} to={`/brand/${encodeURIComponent(b.name)}`} data-testid={`brand-${b.slug}`} className="px-4 py-2 bg-white border border-border rounded-full text-sm font-medium text-steel hover:border-graphite hover:text-graphite transition-colors">
              {b.name} <span className="mono text-xs text-steel-light">{b.count}</span>
            </Link>
          ))}
        </div>
      </Section>

      {/* INDUSTRIAL BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
        <div className="relative overflow-hidden rounded-lg bg-graphite p-8 sm:p-12">
          <div className="relative max-w-xl">
            <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Built for the workshop floor.</h3>
            <p className="text-white/70 mt-3">Enable <span className="text-orange font-medium">Technician Mode</span> for large touch targets, one-handed operation and quick reordering of your most used parts.</p>
            <Link to="/catalogue" className="inline-flex items-center gap-2 mt-6 bg-orange hover:bg-orange-hover text-white font-medium rounded-md px-5 h-11 transition-colors">
              Browse Catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <TechnicianBar />
    </div>
  );
};

export default Home;
